import { Link, useLocation } from 'react-router'
import { useSidebar } from '@/context/SidebarContext.jsx'
import { useAuth } from '@/context/AuthContext'
import {Disclosure, DisclosureButton, DisclosurePanel} from '@headlessui/react'
import {
    logoLatest,
    sidebarHelp,
    sidebarPayment,
    sidebarHome,
    sidebarWebsite,
    sidebarNotification,
    sidebarPromotions,
    sidebarRides,
    sidebarSettings,
    SidebarBecomeDriver,
    plusIcon,
    plusIconLight,
    carIcon,
    clockIcon,
    doneIcon,
    cancelCircleIcon
} from '@img/index.js'
import NotifyDot from '@/customComponents/uiComponents/global/NotifyDot.jsx'
import {useUnreadCount} from '@/hooks/useNotifications'
import {useCategoryCount} from '@/hooks/useTrips'

function formatBadge(count) {
    if (!count) return null
    return count > 99 ? '99+' : count
}

function useNavItems() {
    const {data: unreadCount} = useUnreadCount()
    const {data: upcomingCount} = useCategoryCount('upcoming')
    const ridesBadge = formatBadge(upcomingCount)
    const notifBadge = formatBadge(unreadCount)

    return [
        { icon: sidebarHome, label: 'Home', path: '/' },
        // External link to the main website (booking portal) — env-only
        // (VITE_BOOKING_PORTAL_URL): dev build → local site, production build → live.
        { icon: sidebarWebsite, label: 'Website', href: import.meta.env.VITE_BOOKING_PORTAL_URL },
        {
            icon: sidebarRides, label: 'My Rides', badge: ridesBadge, showNotification: !!ridesBadge,
            children: [
                { label: 'Upcoming', path: '/rides/upcoming', icon: clockIcon },
                { label: 'On Going', path: '/rides/ongoing', icon: carIcon },
                { label: 'Completed', path: '/rides/completed', icon: doneIcon },
                { label: 'Cancelled', path: '/rides/cancelled', icon: cancelCircleIcon },
            ]
        },
        { icon: sidebarPayment, label: 'Payment & Credit', path: '/payment' },
        { icon: sidebarPromotions, label: 'Promotions & Loyalty', path: '/promotions' },
        { icon: sidebarNotification, label: 'Notifications', path: '/notifications', badge: notifBadge, showNotification: !!notifBadge },
        { icon: sidebarHelp, label: 'Help Center', path: '/help' },
        { icon: sidebarSettings, label: 'Settings', path: '/settings' }
    ]
}

function Sidebar() {
    const { isMobileOpen, closeMobileSidebar, isCollapsed, toggleCollapse } = useSidebar()
    const { logout } = useAuth()
    const location = useLocation()
    const navItems = useNavItems()

    const hideOnCollapse = isCollapsed ? 'xl:hidden' : ''
    const centerOnCollapse = isCollapsed ? 'xl:justify-center' : ''

    const handleLogout = () => {
        closeMobileSidebar()
        logout()
    }

    return (
        <aside
            className={`rounded-[0_24px_24px_0] fixed top-0 left-0 flex flex-col h-[100dvh] w-72.5 bg-(--bg-dark) text-white transition-[translate,width] duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform z-50 shadow-[8px_0_24px_rgba(0,0,0,0.18)]
                ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
                ${isCollapsed ? 'xl:w-20' : ''}
                xl:translate-x-0 xl:shadow-none`}
        >
            {/* Logo + collapse burger (right of logo) */}
            <div className={`pt-8 pb-10 px-7 max-md:pt-6 max-md:pb-6 flex items-center justify-between gap-3 ${isCollapsed ? 'xl:px-0 xl:justify-center' : ''}`}>
                <Link to="/" onClick={closeMobileSidebar} className="outline-0">
                    <img alt="logo" src={logoLatest} className={`brightness-0 invert max-md:h-9 max-md:w-auto ${hideOnCollapse}`} />
                </Link>
                <button type="button" onClick={toggleCollapse} aria-label="Toggle sidebar"
                        className="hidden xl:flex items-center justify-center w-12 h-12 rounded-xl cursor-pointer text-white shrink-0">
                    <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M0.583252 1C0.583252 0.585788 0.919038 0.25 1.33325 0.25H14.6666C15.0808 0.25 15.4166 0.585786 15.4166 1C15.4166 1.41421 15.0808 1.75 14.6666 1.75L1.33325 1.75C0.919038 1.75 0.583252 1.41422 0.583252 1ZM0.583252 11C0.583252 10.5858 0.919038 10.25 1.33325 10.25L14.6666 10.25C15.0808 10.25 15.4166 10.5858 15.4166 11C15.4166 11.4142 15.0808 11.75 14.6666 11.75L1.33325 11.75C0.919038 11.75 0.583252 11.4142 0.583252 11ZM1.33325 5.25C0.919038 5.25 0.583252 5.58579 0.583252 6C0.583252 6.41421 0.919038 6.75 1.33325 6.75L7.99992 6.75C8.41413 6.75 8.74992 6.41421 8.74992 6C8.74992 5.58579 8.41413 5.25 7.99992 5.25L1.33325 5.25Z" fill="currentColor"/>
                    </svg>
                </button>
            </div>

            {/* Nav + Book Ride */}
            <div className="flex flex-col flex-1 overflow-y-auto sidebar-scroll">
                <nav className="flex flex-col gap-3 px-5 pb-5 pr-0 max-md:gap-2">
                    {navItems.map((item) => {
                        if (item.children) {
                            const isChildActive = item.children.some(c => location.pathname === c.path)
                            return (
                                <Disclosure as="div" key={`${item.label}-${isChildActive ? 'child' : location.pathname}`} defaultOpen={isChildActive}>
                                    {({open}) => {
                                        const grouped = isCollapsed && open
                                        return (
                                            <>
                                                <DisclosureButton
                                                    className={`group flex items-center gap-3 px-4 py-3 rounded-[24px_0_0_24px] transition-all duration-200 text-[18px] max-md:py-2.5 max-md:text-[17px] w-full cursor-pointer ${centerOnCollapse} ${grouped ? 'xl:rounded-bl-none' : ''}
                                                        ${isChildActive ? 'bg-[#373437]' : grouped ? 'xl:bg-[#373437]' : 'bg-transparent'}`}
                                                >
                                                    <span className="relative"><img alt={item.label} src={item.icon} className="max-md:h-5 max-md:w-5 object-contain"/> <NotifyDot show={item.showNotification} /></span>
                                                    <span className={`flex-1 text-left ${hideOnCollapse}`}>{item.label}</span>
                                                    {item.badge && (
                                                        <span className={`bg-white text-black text-[13px] font-bold min-w-5 px-1 h-5 rounded-full max-md:h-4 max-md:min-w-4 max-md:text-[10px] flex items-center justify-center ${hideOnCollapse}`}>
                                                            {item.badge}
                                                        </span>
                                                    )}
                                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" className={`shrink-0 mr-4 transition-transform duration-200 group-data-[open]:rotate-180 ${hideOnCollapse}`}>
                                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                </DisclosureButton>
                                                <DisclosurePanel transition className="origin-top transition duration-200 ease-out data-[closed]:opacity-0 data-[closed]:scale-y-0">
                                                    <div className={`flex flex-col gap-1 pl-14 pr-4 mt-1 ${isCollapsed ? 'xl:pl-2 xl:pr-2' : ''} ${grouped ? 'xl:bg-[#373437] xl:rounded-bl-[24px] xl:mt-0 xl:pb-2' : ''}`}>
                                                        {item.children.map((child) => (
                                                            <Link
                                                                key={child.path}
                                                                to={child.path}
                                                                onClick={closeMobileSidebar}
                                                                className={`flex items-center gap-3 px-4 py-2 rounded-full text-[15px] max-md:text-[15px] transition-all duration-200 ${centerOnCollapse}
                                                                    ${location.pathname === child.path ? `bg-[#373437] font-medium ${grouped ? 'xl:bg-white/20' : ''}` : 'opacity-70 hover:opacity-100'}`}
                                                            >
                                                                <img src={child.icon} alt="" className="w-4 h-4 shrink-0 brightness-0 invert"/>
                                                                <span className={hideOnCollapse}>{child.label}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </DisclosurePanel>
                                            </>
                                        )
                                    }}
                                </Disclosure>
                            )
                        }

                        if (item.href) {
                            return (
                                <a
                                    key={item.label}
                                    href={item.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={closeMobileSidebar}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-[24px_0_0_24px] transition-all duration-200 text-[18px] max-md:py-2.5 max-md:text-[17px] bg-transparent ${centerOnCollapse}`}
                                >
                                    <span className="relative"><img alt={item.label} src={item.icon} className="max-md:h-5 max-md:w-5 object-contain"/></span>
                                    <span className={hideOnCollapse}>{item.label}</span>
                                </a>
                            )
                        }

                        const isActive = location.pathname === item.path
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={closeMobileSidebar}
                                className={`flex items-center gap-3 px-4 py-3 rounded-[24px_0_0_24px] transition-all duration-200 text-[18px] max-md:py-2.5 max-md:text-[17px] ${centerOnCollapse}
                                    ${isActive ? 'bg-[#373437]' : 'bg-transparent'}`}
                            >
                                <span className="relative"><img alt={item.label} src={item.icon} className="max-md:h-5 max-md:w-5 object-contain"/> <NotifyDot show={item.showNotification} /></span>
                                <span className={hideOnCollapse}>{item.label}</span>
                                {item.badge && (
                                    <span className={`bg-white text-black text-[13px] font-bold min-w-5 h-5 px-1.5 rounded-full max-md:h-4 max-md:min-w-4 max-md:px-1 max-md:text-[10px] flex items-center justify-center ${hideOnCollapse}`}>
                                        {item.badge}
                                    </span>
                                )}
                            </Link>
                        )
                    })}
                </nav>

                {/* Book Ride */}
                <div className="flex px-5 pb-6 justify-center max-md:pb-4">
                    <Link to="/book-ride" onClick={closeMobileSidebar} className={`cursor-pointer flex items-center justify-center gap-3 pr-10 py-2 px-3 bg-white text-(--bg-dark) font-semibold rounded-full max-md:gap-2 max-md:py-1.5 max-md:pr-6 max-md:text-sm transition-colors hover:opacity-80 ${isCollapsed ? 'xl:bg-transparent xl:p-0' : ''}`}>
                        <img alt="book ride" src={plusIcon} className={`max-md:h-7 max-md:w-7 ${hideOnCollapse}`}/>
                        <img alt="book ride" src={plusIconLight} className={`hidden ${isCollapsed ? 'xl:inline' : ''}`}/>
                        <span className={hideOnCollapse}>Book Ride</span>
                    </Link>
                </div>
            </div>

            {/* Logout */}
            <div className={`flex px-5 py-10 max-md:py-6 justify-end ${isCollapsed ? 'xl:justify-center' : ''}`}>
                <button type="button" onClick={handleLogout} className="cursor-pointer flex items-center gap-3 font-light text-white tracking-wide max-md:text-base">
                    <span className={hideOnCollapse}>Logout</span>
                    <img alt="logout" src={SidebarBecomeDriver} className="max-md:h-5 max-md:w-5 object-contain"/>
                </button>
            </div>
        </aside>
    )
}

export default Sidebar
